import { TextField } from '../primitives/index.js'
import { Typography } from '../primitives/index.js'
import { FolderOpen, Search } from '@tunasong/icons'
import type { Entity, Persisted } from '@tunasong/schemas'
import React, { useEffect, useState } from 'react'
import { useFavorites } from '../favorites/favorites.hook.js'
import { useListKeyboardNav } from '../hooks/list-keyboard-nav.js'
import type { NavigationOptions } from '../navigation/navigate.js'
import { EntityListItem } from './entity-list-item.js'
import { ListSkeleton } from './list-skeleton.js'
import { List, type ListProps } from '../primitives/index.js'
import styles from './entity-list.module.css'

export interface EntityListProps extends ListProps {
  className?: string
  ref?: React.Ref<HTMLUListElement>
  entities: Persisted<Entity>[]
  selected?: Persisted<Entity>[]
  controls?: boolean
  size?: 'tiny' | 'small' | 'large' | 'medium'
  placeholder?: string
  actions?: boolean
  showBreadcrumbs?: boolean | 'inactiveLinks'
  /** Show loading skeleton */
  loading?: boolean
  /** Show search input */
  searchable?: boolean
  /** Phase 2: forward through to each row to gate the last-editor avatar/dot. */
  showLastEditor?: boolean
  /** Phase 2 a11y: optional prefix for each row's accessible name (e.g. "Pinned: "). */
  itemAriaLabelPrefix?: string

  onOpen?(entity: Persisted<Entity>, options: NavigationOptions): void
}

export const EntityList = (props: EntityListProps) => {
  const {
    className,
    ref,
    entities,
    actions,
    controls: _controls,
    selected = [],
    size = 'small',
    showBreadcrumbs = false,
    loading = false,
    searchable = false,
    showLastEditor,
    itemAriaLabelPrefix,
    onOpen,
    ...restProps
  } = props

  const [searchQuery, setSearchQuery] = useState('')

  const handleOpen = (entity: Persisted<Entity>) => (ev: React.MouseEvent<HTMLLIElement>) => {
    if (!onOpen) {
      return
    }
    ev.preventDefault()
    ev.stopPropagation()
    const navigationMode = ev.shiftKey ? 'dialog' : 'page'
    onOpen(entity, { navigationMode })
  }

  const { isFavorite } = useFavorites()

  // Filter out trashed items and sort favorites to the top
  const items = entities
    .filter(e => !e.trash)
    .filter(e => {
      if (!searchQuery) {
        return true
      }
      const query = searchQuery.toLowerCase()
      return (
        e.name?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query) ||
        e.type.toLowerCase().includes(query)
      )
    })
    .sort((a, b) => {
      const aFav = isFavorite(a)
      const bFav = isFavorite(b)
      if (aFav && !bFav) {
        return -1
      }
      if (!aFav && bFav) {
        return 1
      }
      return 0
    })

  const selectedIds = new Set(selected.map(e => e.id))

  const { listProps, getItemRef, resetFocus } = useListKeyboardNav({ items, onOpen })

  useEffect(() => {
    resetFocus()
  }, [resetFocus, searchQuery])

  if (loading) {
    return <ListSkeleton numItems={5} />
  }

  const emptyState = (
    <div className={styles.emptyState}>
      <FolderOpen size={48} className={styles.emptyIcon} />
      <Typography variant="body2">{restProps.placeholder ?? 'No items found'}</Typography>
      {searchQuery && (
        <Typography variant="caption" className={styles.emptyHint}>
          Try a different search term
        </Typography>
      )}
    </div>
  )

  return (
    <div className={styles.root}>
      {searchable && (
        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search size={18} className={styles.searchIcon} />,
          }}
          className={styles.searchField}
        />
      )}
      {items.length === 0 ? (
        emptyState
      ) : (
        <List ref={ref} className={className} {...listProps} {...restProps}>
          {items.map((element, index) => (
            <EntityListItem
              key={element.id}
              ref={getItemRef(index)}
              size={size}
              favorite={isFavorite(element)}
              actions={actions}
              selected={selectedIds.has(element.id)}
              element={element}
              showBreadcrumbs={showBreadcrumbs}
              showLastEditor={showLastEditor}
              itemAriaLabelPrefix={itemAriaLabelPrefix}
              onClick={handleOpen(element)}
            />
          ))}
        </List>
      )}
    </div>
  )
}
